export interface DarkAndLight { dark: string, light: string }
const sameForDarkAndLight = (val: string): DarkAndLight => ({ dark: val, light: val })

export const GENERIC_COLORS: Record<string, DarkAndLight> = {
  text: { light: "#29293a", dark: "#C0C1C6" },
  panelBg: {light: "#FFFFFF", dark: "#29293A" },
  textLight: { light: "#71737F", dark: "#C0C1C6" },
  countdownBullet: { light: "#29293a55", dark: "#C0C1C655" },
  countdownBulletMuchTime: { light: "#6CB87F", dark: "#6CB87F" },
  countdownBulletAlmostOver: { light: "#FF5A5A", dark: "#FF4C4C" },
  textHighlight: { light: "#009990", dark: "#69c1b8" },
  buttonBorder: { light: "#29293a", dark: "#FFFFFF" },
  buttonBg: { light: "#29293a", dark: "#FFFFFF" },
  buttonText: { light: "#FFFFFF", dark: "#29293a" },
  buttonSecondaryBorder: { light: "#29293a", dark: "#FFFFFF" },
  buttonSecondaryBg: { light: "#FFFFFF", dark: "#29293a" },
  buttonSecondaryText: { light: "#29293a", dark: "#FFFFFF" }, 
  // answerBorder: { light: "#e0e0e3", dark: "#71737f" },
  answerBorder: { light: "#29293A33", dark: "#C0C1C633" },
  answerBorderActive: { light: "#29293A", dark: "#C0C1C6" },
  answerBg: { light: "#FFFFFF", dark: "#29293a" },
  answerText: { light: "#29293a", dark: "#C0C1C6" },
  goodText: { light: "#6CB87F", dark: "#6CB87F" },
  goodBorder: { light: "#6CB87F", dark: "#6CB87F" },
  goodBg: { light: "#DBFFE4", dark: "#275332" },
  textOnGoodBg: { light: "#29293a", dark: "#C0C1C6" },
  badBorder: { light: "#FF5A5A", dark: "#FF4C4C" },
  badText: { light: "#FF5A5A", dark: "#FF4C4C" },
  badTextForTryAgain: { light: "#FF5A5A", dark: "#C0C1C6" },
  badBg: { light: "#FFD8D8", dark: "#672929" },
  textOnBadBg: { light: "#29293a", dark: "#C0C1C6" },
  rankingHeadText: { light: "#71737f", dark: "#C0C1C6" },
  rankingOddBg: { light: "#F2F2F3", dark: "#000000" },
  rankingEvenBg: { light: "#FFFFFF", dark: "#29293A" },
  rankingOwnBg: { light: "#DBEFD8", dark: "#3B4A37" },
  pairsMarkerType: { light: "#FFFFFF", dark: "#29293a" },
  pairsMarkerUnset: { light: "#29293a", dark: "#C0C1C6" },
  pairsMarker1: { light: "#41645E", dark: "#DDE1AF" },
  pairsMarker2: { light: "#323C60", dark: "#D1D7E5" },
  pairsMarker3: { light: "#823754", dark: "#ffcdc4" },
  pairsMarker4: { light: "#BD5528", dark: "#fff5c3" },
  pairsMarker5: { light: "#6E6991", dark: "#ffcd9d" },
  pairsMarker6: { light: "#6e605a", dark: "#d2bed7" },
} as const

export const GENERIC_COLORS_MAGAZIN: typeof GENERIC_COLORS = {
  ...GENERIC_COLORS,
  buttonBorder: GENERIC_COLORS.buttonSecondaryBorder,
  buttonBg: GENERIC_COLORS.buttonSecondaryBg,
  buttonText: GENERIC_COLORS.buttonSecondaryText,
} as const

const simpleBgForMagazin = (col: Quiz.Api.ThemeColor): Pick<Quiz.Api.QuizColorTheme, "cardBg" | "cardBgIntense" | "cardBgSubtle" | "cardBorder" | "cardBorderSizeDesktop" | "cardBorderSizeMobile" | "roundNumberBg" | "roundNumberProgress"> => ({
  cardBg: col,
  cardBgIntense: col,
  cardBgSubtle: col,
  cardBorder: col,
  cardBorderSizeDesktop: sameForDarkAndLight("2px"),
  cardBorderSizeMobile: sameForDarkAndLight("2px"),
  roundNumberBg: col,
  roundNumberProgress: col,
})

// Used for SZ and Magazin quiz backgrounds
// Cover and Result mostly get a separate fixed scheme, each question can get it's own colorful scheme
export const COLOR_SCHEMES: Record<string, Quiz.Api.QuizColorTheme> = {
  "0": { // only used for COVER! <------ white
    cardBorder: { light: "#e0e0e3", dark: "#71737F" },
    cardBgSubtle: { light: "#e0e0e3", dark: "#71737F" }, // not used on cover
    cardBgIntense: { light: "#e0e0e3", dark: "#71737F" }, // not used on cover
    cardBorderSizeDesktop: sameForDarkAndLight("15px"),
    cardBorderSizeMobile: sameForDarkAndLight("7px"),
    cardBg: {light: "#FFFFFF", dark: "#29293A" },
    roundNumberBg: { light: "#ECB6B6", dark: "#434B41" }, // not used on cover
    roundNumberProgress: { light: "#A1CAC8", dark: "#009990" }, // not used on cover
  },
  "1": { // <------------ "#1 from sd-zeplin
    cardBorder: { light: "#FCF9DD", dark: "#775E44" },
    cardBgSubtle: { light: "#FCF9DD", dark: "#18130E" },
    cardBorderSizeDesktop: sameForDarkAndLight("15px"),
    cardBorderSizeMobile: sameForDarkAndLight("7px"),
    cardBg: { light: "#FEFDF4", dark: "#30251B" },
    cardBgIntense: { light: "#EB8E", dark: "#18130E" },
    roundNumberBg: { light: "#ECB6B6", dark: "#434B41" },
    roundNumberProgress: { light: "#A1CAC8", dark: "#009990" },
  },
  "2": { // <------------ "#2 from sd-zeplin
    cardBorder: { light: "#F4FAF9", dark: "#009990" },
    cardBgSubtle: { light: "#F4FAF9", dark: "#005755" },
    cardBorderSizeDesktop: sameForDarkAndLight("15px"),
    cardBorderSizeMobile: sameForDarkAndLight("7px"),
    cardBg: { light: "#FBFDFD", dark: "#29293A" },
    cardBgIntense: { light: "#D9EEEC", dark: "#005755" },
    roundNumberBg: { light: "#F4EB8E", dark: "#30251B" },
    roundNumberProgress: { light: "#ECB6B6", dark: "#CC9D9D" },
  },
  "3": { // <------------ "#3 from sd-zeplin
    cardBorder: { light: "#F9E9E9", dark: "#8E6D6D" },
    cardBgSubtle: { light: "#F9E9E9", dark: "#181212" },
    cardBorderSizeDesktop: sameForDarkAndLight("15px"),
    cardBorderSizeMobile: sameForDarkAndLight("7px"),
    cardBg: { light: "#FDF8F8", dark: "#2F2424" },
    cardBgIntense: { light: "#ECB6B6", dark: "#181212" },
    roundNumberBg: { light: "#CEEACA", dark: "#434B41" },
    roundNumberProgress: { light: "#A1CAC8", dark: "#009990" },
  },
  "4": { // <------------ "#4 from sd-zeplin
    cardBorder: { light: "#EDF3F9", dark: "#75828C" },
    cardBgSubtle: { light: "#EDF3F9", dark: "#141617" },
    cardBorderSizeDesktop: sameForDarkAndLight("15px"),
    cardBorderSizeMobile: sameForDarkAndLight("7px"),
    cardBg: { light: "#F9FBFD", dark: "#272B2F" },
    cardBgIntense: { light: "#C3D8EA", dark: "#141617" },
    roundNumberBg: { light: "#C3C3E3", dark: "#4E4E5B" },
    roundNumberProgress: { light: "#F4EB8E", dark: "#C7BF6A" },
  },
  "5": { // <------------ "#5 from sd-zeplin
    cardBorder: { light: "#F9E9E9", dark: "#8E6D6D" },
    cardBgSubtle: { light: "#F9E9E9", dark: "#2F2424" },
    cardBorderSizeDesktop: sameForDarkAndLight("15px"),
    cardBorderSizeMobile: sameForDarkAndLight("7px"),
    cardBg: { light: "#FDF8F8", dark: "#5E4949" },
    cardBgIntense: { light: "#ECB6B6", dark: "#2F2424" },
    roundNumberBg: { light: "#F3CB8E", dark: "#5A4A33" },
    roundNumberProgress: { light: "#A1CAC8", dark: "#009990" },
  },
  "6": { // <------------ "#6 from sd-zeplin
    cardBorder: { light: "#FCF9DD", dark: "#928D55" },
    cardBgSubtle: { light: "#FCF9DD", dark: "#312F1C" },
    cardBorderSizeDesktop: sameForDarkAndLight("15px"),
    cardBorderSizeMobile: sameForDarkAndLight("7px"),
    cardBg: { light: "#FEFDF4", dark: "#54502F" },
    cardBgIntense: { light: "#F4EB8E", dark: "#312F1C" },
    roundNumberBg: { light: "#C3D8EA", dark: "#27272D" },
    roundNumberProgress: { light: "#EBB5B5", dark: "#CC9D9D" },
  },
  "7": { // <------------ "#7 from sd-zeplin
    cardBorder: { light: "#F4FAF3", dark: "#838F82" },
    cardBgSubtle: { light: "#F4FAF3", dark: "#2C302B" },
    cardBorderSizeDesktop: sameForDarkAndLight("15px"),
    cardBorderSizeMobile: sameForDarkAndLight("7px"),
    cardBg: { light: "#FBFDFB", dark: "#454E43" },
    cardBgIntense: { light: "#DBEFD8", dark: "#2C302B" },
    roundNumberBg: { light: "#F4EB8E", dark: "#625E39" },
    roundNumberProgress: { light: "#69C1B8", dark: "#009990" },
  },
  "8": { // <------------ "#8 from sd-zeplin
    cardBorder: { light: "#FBEFDD", dark: "#927A55" },
    cardBgSubtle: { light: "#FBEFDD", dark: "#31291C" },
    cardBorderSizeDesktop: sameForDarkAndLight("15px"),
    cardBorderSizeMobile: sameForDarkAndLight("7px"),
    cardBg: { light: "#FEFAF4", dark: "#5A4A33" },
    cardBgIntense: { light: "#F3CB8E", dark: "#31291C" },
    roundNumberBg: { light: "#C3D8EA", dark: "#272B2F" },
    roundNumberProgress: { light: "#F4EB8E", dark: "#C7BF6A" },
  },
  "9": { // <------------ "#9 from sd-zeplin
    cardBorder: { light: "#EDEDF7", dark: "#757588" },
    cardBgSubtle: { light: "#EDEDF7", dark: "#27272D" },
    cardBorderSizeDesktop: sameForDarkAndLight("15px"),
    cardBorderSizeMobile: sameForDarkAndLight("7px"),
    cardBg: { light: "#F9F9FC", dark: "#4E4E5B" },
    cardBgIntense: { light: "#C3C3E3", dark: "#27272D" },
    roundNumberBg: { light: "#DBEFD8", dark: "#434B41" },
    roundNumberProgress: { light: "#69C1B8", dark: "#CC9D9D" },
  },
  "10": { // <------------ "#10 from sd-zeplin
    cardBorder: { light: "#F4FAF3", dark: "#838F82" },
    cardBgSubtle: { light: "#F4FAF3", dark: "#2C302B" },
    cardBorderSizeDesktop: sameForDarkAndLight("15px"),
    cardBorderSizeMobile: sameForDarkAndLight("7px"),
    cardBg: { light: "#FBFDFB", dark: "#434B41" },
    cardBgIntense: { light: "#DBEFD8", dark: "#2C302B" },
    roundNumberBg: { light: "#ECB6B6", dark: "#5E4949" },
    roundNumberProgress: { light: "#FFFCDB", dark: "#C7BF6A" },
  },
  "magazin1": {
    ...simpleBgForMagazin({light: "#FFF5C3", dark: "#604A00"}),
  },
  "magazin2": {
    ...simpleBgForMagazin({light: "#D2D7E6", dark: "#323C5F"}),
  },
  "magazin3": {
    ...simpleBgForMagazin({light: "#F0EBE6", dark: "#554B4B"}),
  },
  "magazin4": {
    ...simpleBgForMagazin({light: "#FFE6CF", dark: "#7B3F24"}),
  },
  "magazinResult": {
    ...simpleBgForMagazin({light: "#FFFFFF", dark: "#29293a" }),
    cardBorder: {light: "#29293A", dark: "#C0C1C6" },
  },
} as const
